import React, { useState, useEffect, useMemo } from "react"
import withStyles from "react-jss"
import { Question, JumpingDots } from "../components"
import { data } from "../data/data"
import { RESULT_IDS } from "../data/uniqueValues"
import { FADEOUT } from "../data/globalStyles"
import {
  formatUserChoicesData,
  formatShowNewQuestion,
  getNextQuestionWithOptions,
  getImagesFromResultIds,
} from "../utils/helper"
import { preloadImages } from "../utils/preloadImages"
import {
  QUIZ_FADEIN,
  QUIZ_FADEOUT,
  SECTION_DURATION,
} from "../data/globalStyles"
import { gourds } from "../assets/images"

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    height: "100vh",
    animation: ({ show }) =>
      `${
        show ? `$${QUIZ_FADEIN}` : `$${QUIZ_FADEOUT}`
      } ${SECTION_DURATION} ease-in-out`,
  },
  results: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    background: "white",
    color: "black",
    borderBottom: "1px solid black",
    padding: "1rem",
  },
  [`@keyframes ${QUIZ_FADEIN}`]: {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  [`@keyframes ${QUIZ_FADEOUT}`]: {
    from: {
      opacity: 1,
    },
    to: {
      opacity: 0,
    },
  },
}

const Quiz = ({
  classes,
  setShow,
  section,
  onAnimationEnd,
  setCustomizedMenu,
  resetQuiz,
  setResetQuiz,
  setGiftFor,
}) => {
  const INITIAL_USER_CHOICE_DATA = useMemo(
    () => formatUserChoicesData([], data[0]),
    []
  )

  // Master list containing all user choices
  const [userChoices, setUserChoices] = useState(() => INITIAL_USER_CHOICE_DATA)

  const [renderData, setRenderData] = useState(userChoices)
  const [nextQ, setNextQ] = useState({})

  const [possibleResults, setPossibleResults] = useState(RESULT_IDS)
  const [showResults, setShowResults] = useState(false)

  const updateChoice = (
    qId,
    selectedOptionId,
    newUserChoices,
    newFilteredPossibleResults
  ) => {
    let selectedOptionData = {},
      updatedUserChoices = [],
      filteredPossibleResults = []

    // `newUserChoices` is set only when there's a question
    // with only one option. Using this as `userChoices` update
    // happens async and this function ends up running before the update
    if (newUserChoices) {
      // Find the selected answer in userChoices
      selectedOptionData = newUserChoices
        .find((d) => d.qId === qId)
        .options.find((d) => d.optionId === selectedOptionId)

      // Update the user's choice
      updatedUserChoices = newUserChoices.map((d) => {
        if (d.qId === qId) {
          return {
            ...d,
            options: d.options.map((option) => ({
              ...option,
              selected: selectedOptionId === option.optionId ? true : false,
            })),
          }
        } else return d
      })
    } else {
      // Find the selected answer in userChoices
      selectedOptionData = userChoices
        .find((d) => d.qId === qId)
        .options.find((d) => d.optionId === selectedOptionId)

      // Update the user's choice
      updatedUserChoices = userChoices.map((d) => {
        if (d.qId === qId) {
          return {
            ...d,
            options: d.options.map((option) => ({
              ...option,
              selected: selectedOptionId === option.optionId ? true : false,
            })),
          }
        } else return d
      })
    }

    if (qId === 2) {
      setGiftFor(selectedOptionData.text)
    }

    // `newFilteredPossibleResults` is set only when there's a question
    // with only one option. Using this as `possibleResults` update
    // happens async and this function ends up running before the update
    if (newFilteredPossibleResults) {
      // Filter possible results for the user
      filteredPossibleResults = newFilteredPossibleResults.filter((id) =>
        selectedOptionData.possibleResultsIds.includes(id)
      )
    } else {
      // Filter possible results for the user
      filteredPossibleResults = possibleResults.filter((id) =>
        selectedOptionData.possibleResultsIds.includes(id)
      )
    }
    setPossibleResults(filteredPossibleResults)

    let nextQId = selectedOptionData.nextQId

    // If the quiz hasn't ended and there are next questions to ask
    if (nextQId) {
      const nextQuestion = data.find((d) => d.qId === nextQId)

      // Skip questions that don't have relevant options to
      // previous user choices
      const { question: nextQuestion2, skipToResults } =
        getNextQuestionWithOptions(
          nextQuestion,
          filteredPossibleResults,
          nextQId,
          data
        )

      // If skipping questions from prev step reach the end of quiz
      // display the result
      if (skipToResults) {
        displayResults(filteredPossibleResults)
      }

      const formattedNextQuestion = formatShowNewQuestion(nextQuestion2)

      // If the next question only has one option, make the choice
      // for the user and show the next question
      if (formattedNextQuestion.options.length === 1) {
        const choice = formattedNextQuestion.options[0]
        if (choice.nextQId) {
          const newUserChoiceWithNextQ = formatUserChoicesData(
            updatedUserChoices,
            formattedNextQuestion
          )
          // Call with 3rd parameter as state update for
          // `userChoice` will be async and won't get updated
          // before this function is called
          updateChoice(
            formattedNextQuestion.qId,
            choice.optionId,
            newUserChoiceWithNextQ,
            filteredPossibleResults
          )
          return
        }
        // If there is no nextQId, then show the result to user
        else {
          // Filter possible results for the choice made
          const finalResultIds = filteredPossibleResults.filter((id) =>
            choice.possibleResultsIds.includes(id)
          )
          setPossibleResults(finalResultIds)
          displayResults(finalResultIds)
          return
        }
      }

      setNextQ(formattedNextQuestion)

      const userChoiceWithNextQ = formatUserChoicesData(
        updatedUserChoices,
        formattedNextQuestion
      )
      setUserChoices(userChoiceWithNextQ)

      // Hide the old question, which will trigger onAnimationEnd()
      setRenderData((data) => [
        {
          ...data[0],
          showQuestion: false,
        },
      ])
    } else {
      displayResults(filteredPossibleResults)
    }

    if (window.parent && window.parent.dataLayer) {
      window.parent.dataLayer.push({
        event: `datavisualization-HolidayGiftGuide-answerButton-${
          userChoices.length > 1 ? userChoices[1].text : userChoices[0].text
        }`,
        eventLabel: `${selectedOptionData.text}`,
        "gtm.elementLabel": selectedOptionData.text,
      })
    }
  }

  const displayResults = (filteredPossibleResults) => {
    // Preload result images for better UX
    const images = getImagesFromResultIds(filteredPossibleResults)
    preloadImages(images)

    setShowResults(true)
    // Hide the old question, which will trigger onAnimationEnd()
    setRenderData((data) => [
      {
        ...data[0],
        showQuestion: false,
      },
    ])
    setCustomizedMenu(filteredPossibleResults)
  }

  const loadingText = () => {
    return (
      <span>
        Curating your gift <JumpingDots />
      </span>
    )
  }

  // When user clicks on an option, it triggers CSS animation.
  // This function gets triggered when it ends
  const onQuizAnimationEnd = ({ animationName }) => {
    // Hide quiz section and show results
    if (
      showResults &&
      renderData[0].showResult &&
      animationName.includes(FADEOUT)
    ) {
      // setRenderData([])
      // setShow((d) => ({
      //   ...d,
      //   quiz: false,
      //   results: true,
      // }))
    }
    // If menu is finalized, show loading for 3s, then switch to result
    else if (showResults && animationName.includes(FADEOUT)) {
      setRenderData([
        {
          loading: true,
          text: loadingText(),
          showQuestion: true,
          showResult: false,
        },
      ])
      setTimeout(() => {
        setShow((d) => ({
          ...d,
          quiz: false,
          results: true,
        }))
      }, 2900)
      // Show next question
    } else if (animationName.includes(FADEOUT)) {
      const formattedQ = formatShowNewQuestion(nextQ)
      setRenderData([formattedQ])
    }
  }

  useEffect(() => {
    if (resetQuiz) {
      setUserChoices(INITIAL_USER_CHOICE_DATA)
      setRenderData(INITIAL_USER_CHOICE_DATA)
      setNextQ({})
      setPossibleResults(RESULT_IDS)
      setShowResults(false)
      setResetQuiz(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetQuiz])

  useEffect(() => {
    // Preload loading GIF
    preloadImages([gourds])
  }, [])

  return (
    section === "quiz" && (
      <section className={classes.container} onAnimationEnd={onAnimationEnd}>
        <Question
          data={renderData}
          onAnimationEnd={onQuizAnimationEnd}
          onClick={updateChoice}
        />
        {/* For debugging */}
        {/* <div className={classes.results}>
          {possibleResults ? possibleResults.join(", ") : "No results"}
        </div> */}
      </section>
    )
  )
}

export default withStyles(styles)(Quiz)
